// Hand-rolled SVG chart layer for the Analytics section.
//
// Why SVG and not a charting lib: React is a CDN global here (not bundled), so
// React-based libs (Recharts/Nivo) would pull a 2nd React. Canvas libs
// (Chart.js/ECharts) don't theme off our CSS variables. Hand-rolled SVG costs
// zero bundle, themes natively (fills are var(--accent) etc. so light/dark +
// accent switches recolor for free), and matches the Sparkline/HealthBar
// precedent. The AI returns already-aggregated data (tens–hundreds of points),
// so SVG performance is a non-issue.
//
// WidgetRenderer({ spec, data }) is the single entry point: it maps the AI
// spec ({chart_type, x, y, series, agg, value_format, table_columns}) onto the
// right renderer. data = { columns: [...], rows: [{...}] }.

// ── value formatting ────────────────────────────────────────────────────────
const _num = (v) => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtVal = (v, fmt) => {
  const n = _num(v);
  if (n == null) return v == null || v === '' ? '—' : String(v);
  if (fmt === 'currency') return window.fmtMoney(n);
  if (fmt === 'percent') return `${Math.round(n * 10) / 10}%`;
  if (fmt === 'duration_days') return `${n.toLocaleString()}d`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// Compact axis labels: 1.2M / 3.4K.
const fmtAxis = (n, fmt) => {
  if (fmt === 'currency') return window.fmtMoney(n);
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(Math.round(n * 100) / 100);
};

// Theme-aware series palette — accent first, then the HUE dot colors. These are
// oklch literals that read well in both light and dark.
const PALETTE = [
  'var(--accent)',
  'oklch(0.55 0.10 200)', // teal
  'oklch(0.55 0.13 295)', // violet
  'oklch(0.65 0.13 75)',  // amber
  'oklch(0.55 0.10 155)', // green
  'oklch(0.55 0.14 25)',  // rose
  'oklch(0.55 0.12 255)', // blue
  'oklch(0.55 0.01 270)', // slate
];
const seriesColor = (i) => PALETTE[i % PALETTE.length];

// ── human-readable column names + relationship links ─────────────────────────
const _ACRONYMS = { id: 'ID', hq: 'HQ', gpp: 'GPP', loi: 'LOI', ioi: 'IOI', ds: 'D/S', bs: 'B/S', ebitda: 'EBITDA', ceo: 'CEO', cfo: 'CFO', url: 'URL', dnc: 'DNC', mrr: 'MRR', arr: 'ARR', ytd: 'YTD', qtd: 'QTD', ai: 'AI', api: 'API', sql: 'SQL', kpi: 'KPI', roi: 'ROI', crm: 'CRM', vp: 'VP', usa: 'USA', us: 'US', pct: '%' };
// "company_name" → "Company name", "hq_state" → "HQ state", "estimated_fees" → "Estimated fees"
const humanizeCol = (name) => {
  if (name == null) return '';
  const toks = String(name).trim().split(/[_\s]+/).filter(Boolean);
  if (!toks.length) return String(name);
  const words = toks.map((t) => _ACRONYMS[t.toLowerCase()] || t.toLowerCase());
  if (!_ACRONYMS[toks[0].toLowerCase()]) words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(' ');
};

// Relationship prefix → window.AtlasNav method. company/contact/engagement open
// their detail pages; dealmaker opens the team-member profile.
const _NAV = { company: 'goToCompany', contact: 'goToContact', engagement: 'goToDeal', deal: 'goToDeal', dealmaker: 'goToUser', buyer: 'goToBuyer' };
// A source/view name → the entity its rows represent (for the bare id/name pair).
const sourceEntity = (src) => {
  const s = String(src || '').toLowerCase();
  if (!s) return null;
  if (s.includes('eng')) return 'engagement';
  if (s.includes('compan')) return 'company';
  if (s.includes('contact')) return 'contact';
  return null;
};
// If a cell links to a record page, return {method, id}; else null. A column
// `<x>_name` links via sibling `<x>_id`; the bare `name` links via `id` (or the
// record-list PK alias `__id`) using the entityHint (the source's own entity).
const cellLink = (colKey, row, colSet, entityHint) => {
  const m = /^(.*)_name$/.exec(colKey);
  if (m && _NAV[m[1]] && colSet.has(m[1] + '_id') && row[m[1] + '_id'] != null) {
    return { method: _NAV[m[1]], id: row[m[1] + '_id'], name: row[colKey] };
  }
  if (colKey === 'name' && entityHint && _NAV[entityHint]) {
    const idv = row.id != null ? row.id : row.__id;
    if (idv != null) return { method: _NAV[entityHint], id: idv, name: row[colKey] };
  }
  return null;
};
const navTo = (method, id, name) => { try { window.AtlasNav && window.AtlasNav[method] && window.AtlasNav[method](id, name); } catch (e) { /* no-op */ } };

// ── data transforms ─────────────────────────────────────────────────────────
const aggregate = (rows, key, fn = 'sum') => {
  const vals = rows.map((r) => _num(r[key])).filter((v) => v != null);
  if (fn === 'count') return rows.length;
  if (!vals.length) return null;
  if (fn === 'avg') return vals.reduce((a, b) => a + b, 0) / vals.length;
  if (fn === 'min') return Math.min(...vals);
  if (fn === 'max') return Math.max(...vals);
  if (fn === 'median') {
    const s = [...vals].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }
  return vals.reduce((a, b) => a + b, 0); // sum
};

// rows → { categories, series:[{name, values}] }.
//  • seriesKey set → pivot: one series per distinct seriesKey value, measured by yKey
//  • else          → one series per yKey column
const pivot = (rows, xKey, yKeys, seriesKey) => {
  const cats = [];
  const seen = new Set();
  for (const r of rows) {
    const c = r[xKey];
    const k = String(c);
    if (!seen.has(k)) { seen.add(k); cats.push(c); }
  }
  if (seriesKey) {
    const yKey = (yKeys && yKeys[0]) || null;
    const names = [];
    const nameSeen = new Set();
    for (const r of rows) {
      const s = String(r[seriesKey] ?? '—');
      if (!nameSeen.has(s)) { nameSeen.add(s); names.push(s); }
    }
    const series = names.map((name) => ({
      name,
      values: cats.map((c) => {
        const match = rows.filter((r) => String(r[xKey]) === String(c) && String(r[seriesKey] ?? '—') === name);
        return match.length ? aggregate(match, yKey, 'sum') : null;
      }),
    }));
    return { categories: cats, series };
  }
  const keys = (yKeys && yKeys.length) ? yKeys : [];
  const series = keys.map((yk) => ({
    name: yk,
    values: cats.map((c) => {
      const match = rows.filter((r) => String(r[xKey]) === String(c));
      return match.length ? aggregate(match, yk, 'sum') : null;
    }),
  }));
  return { categories: cats, series };
};

// Infer x / y / series when the spec leaves them null. First non-numeric column
// is the dimension; numeric columns are measures.
const inferAxes = (spec, columns, rows) => {
  const sample = rows.slice(0, 12);
  const isNum = (c) => {
    const vals = sample.map((r) => r[c]).filter((v) => v != null);
    return vals.length > 0 && vals.every((v) => _num(v) != null);
  };
  const numeric = columns.filter(isNum);
  const dims = columns.filter((c) => !numeric.includes(c));
  const x = spec.x || dims[0] || columns[0] || null;
  let y = (spec.y && spec.y.length) ? spec.y : null;
  if (!y) {
    const firstNum = numeric.find((c) => c !== x);
    y = firstNum ? [firstNum] : (numeric[0] ? [numeric[0]] : []);
  }
  return { x, y, series: spec.series || null };
};

// ── layout hook ──────────────────────────────────────────────────────────────
const useSize = () => {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);
  React.useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') {
      if (ref.current) setW(ref.current.clientWidth || 600);
      return;
    }
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.max(0, Math.floor(e.contentRect.width)));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

// ── legend interactivity ─────────────────────────────────────────────────────
// Ephemeral per-chart series visibility (no persistence). `hidden` holds
// ORIGINAL series indices: charts filter their data by it but keep coloring
// and onPick on the original index, so colors and drill selections never shift
// when other series are hidden.
const useSeriesToggle = (names) => {
  const [hidden, setHidden] = React.useState(() => new Set());
  const [hot, setHot] = React.useState(null);          // legend-hovered original index
  const key = names.join('\u0001');
  React.useEffect(() => { setHidden(new Set()); setHot(null); }, [key]);
  const toggle = (i) => {
    setHot(null);                                          // don't leave a hidden series highlighted
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next.size >= names.length ? new Set() : next; // never hide everything
    });
  };
  const isolate = (i) => {
    setHot(null);
    setHidden((prev) => {
      const others = names.map((_, j) => j).filter((j) => j !== i);
      const already = !prev.has(i) && others.every((j) => prev.has(j));
      return already ? new Set() : new Set(others);        // same gesture restores all
    });
  };
  return { hidden, hot, setHot, toggle, isolate };
};

// Click = toggle (deferred a beat so a double-click can cancel it),
// double-click or alt-click = isolate.
const useToggleGestures = (onToggle, onIsolate) => {
  const t = React.useRef(null);
  React.useEffect(() => () => { if (t.current) clearTimeout(t.current); }, []);
  if (!onToggle) return {};
  const fireIsolate = () => { if (t.current) { clearTimeout(t.current); t.current = null; } onIsolate && onIsolate(); };
  return {
    onClick: (e) => {
      if (e.altKey) return fireIsolate();
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => { t.current = null; onToggle(); }, 230);
    },
    onDoubleClick: fireIsolate,
  };
};

const LegendItem = ({ name, color, hidden, onToggle, onIsolate, onHover }) => {
  const gestures = useToggleGestures(onToggle, onIsolate);
  const interactive = !!onToggle;
  return (
    <span {...gestures}
      onMouseEnter={interactive && onHover ? () => onHover(true) : undefined}
      onMouseLeave={interactive && onHover ? () => onHover(false) : undefined}
      title={interactive ? 'Click to toggle · double-click to isolate' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
        color: hidden ? 'var(--text-4)' : 'var(--text-2)',
        textDecoration: hidden ? 'line-through' : 'none',
        cursor: interactive ? 'pointer' : 'default', userSelect: 'none' }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, background: color, opacity: hidden ? 0.3 : 1, flexShrink: 0 }} />
      {name}
    </span>
  );
};

const Legend = ({ items }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 8 }}>
    {items.map((it, i) => <LegendItem key={i} {...it} />)}
  </div>
);

// Series list → interactive legend items wired to a useSeriesToggle instance.
const seriesLegend = (series, tog) => series.map((s, i) => ({
  name: s.name, color: seriesColor(i), hidden: tog.hidden.has(i),
  onToggle: () => tog.toggle(i), onIsolate: () => tog.isolate(i),
  onHover: (on) => tog.setHot(on && !tog.hidden.has(i) ? i : null),
}));

// ── styled tooltip (replaces the native SVG <title> UX) ──────────────────────
// Absolutely positioned inside the chart's relative wrapper, follows the
// cursor; pointerEvents none so it never blocks drill clicks underneath.
const ChartTip = ({ tip, containerW }) => {
  if (!tip || !tip.content) return null;
  const flip = containerW > 0 && tip.x > containerW - 180;
  return (
    <div style={{
      position: 'absolute', top: tip.y + 14, zIndex: 30, pointerEvents: 'none',
      ...(flip ? { right: Math.max(0, containerW - tip.x + 10) } : { left: tip.x + 12 }),
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7,
      boxShadow: 'var(--shadow-lg)', padding: '6px 9px', fontSize: 11.5, lineHeight: 1.5,
      color: 'var(--text-2)', whiteSpace: 'nowrap', maxWidth: 320, overflow: 'hidden',
    }}>
      {tip.content}
    </div>
  );
};

const TipTitle = ({ children }) => (
  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</div>
);

const TipRow = ({ color, name, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    {color && <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />}
    {name != null && <span style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>}
    <span className="num" style={{ marginLeft: 'auto', paddingLeft: 12, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
);

const EmptyChart = ({ message }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120,
    fontSize: 12, color: 'var(--text-3)', padding: 20, textAlign: 'center',
  }}>
    {message || 'No data to display.'}
  </div>
);

// ── Bar (vertical, grouped or stacked) ───────────────────────────────────────
const _pickProps = (onPick, fn) => onPick ? {
  style: { cursor: 'pointer' }, onClick: fn,
  onMouseOver: (e) => { e.currentTarget.style.opacity = 0.78; },
  // Restore to '' (not 1) so the element falls back to its opacity ATTRIBUTE —
  // legend-hover dimming sets that attribute and must win after bar hovers.
  onMouseOut: (e) => { e.currentTarget.style.opacity = ''; },
} : null;

const BarChart = ({ categories, series, stacked, valueFormat, height = 280, onPick }) => {
  const [ref, w] = useSize();
  const tog = useSeriesToggle(series.map((s) => s.name));
  const [tip, setTip] = React.useState(null);            // {ci, x, y} in container coords
  if (!categories.length || !series.length) return <EmptyChart />;
  // Visible series carry their ORIGINAL index si: seriesColor(si) and
  // onPick(ci, si) stay stable while other series are hidden.
  let vis = series.map((se, si) => ({ se, si })).filter((v) => !tog.hidden.has(v.si));
  if (!vis.length) vis = series.map((se, si) => ({ se, si }));
  const padL = 52, padR = 12, padT = 12, padB = 46;
  const W = Math.max(w, 220), H = height;
  const plotW = W - padL - padR, plotH = H - padT - padB;

  const maxVal = stacked
    ? Math.max(1, ...categories.map((_, ci) => vis.reduce((s, v) => s + Math.max(0, _num(v.se.values[ci]) || 0), 0)))
    : Math.max(1, ...vis.flatMap((v) => v.se.values.map((x) => Math.max(0, _num(x) || 0))));
  const ticks = 4;
  const bandW = plotW / categories.length;
  const innerW = bandW * 0.7;
  const x0 = (ci) => padL + ci * bandW + (bandW - innerW) / 2;
  const yOf = (v) => padT + plotH - (v / maxVal) * plotH;
  const showEveryX = Math.ceil(categories.length / Math.max(1, Math.floor(plotW / 60)));
  const dimOf = (si) => (tog.hot != null && tog.hot !== si ? 0.25 : 1);

  // Shared per-category tooltip: one mousemove handler on the svg maps the
  // cursor x to its category band — works over the gaps between bars and never
  // swallows the segment drill clicks (the rects stay on top and clickable).
  const onMove = (e) => {
    const r = ref.current ? ref.current.getBoundingClientRect() : null;
    if (!r) return;
    const x = e.clientX - r.left;
    const ci = Math.floor((x - padL) / bandW);
    if (ci < 0 || ci >= categories.length) { setTip(null); return; }
    setTip({ ci, x, y: e.clientY - r.top });
  };

  const tipContent = tip && (() => {
    const rows = vis.map((v) => ({ v, val: _num(v.se.values[tip.ci]) })).filter((x) => x.val != null);
    if (!rows.length) return null;
    return (
      <div>
        <TipTitle>{String(categories[tip.ci])}</TipTitle>
        {rows.map(({ v, val }) => (
          <TipRow key={v.si} color={seriesColor(v.si)} name={series.length > 1 ? v.se.name : null}
            value={fmtVal(val, valueFormat)} />
        ))}
        {stacked && rows.length > 1 && (
          <TipRow name="Total" value={fmtVal(rows.reduce((s, x) => s + Math.max(0, x.val), 0), valueFormat)} />
        )}
      </div>
    );
  })();

  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      {w > 0 && (
        <svg width={W} height={H} style={{ display: 'block', fontFamily: 'var(--font-ui)' }}
          onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
          {/* gridlines + y labels */}
          {Array.from({ length: ticks + 1 }).map((_, t) => {
            const val = (maxVal / ticks) * t;
            const y = yOf(val);
            return (
              <g key={t}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--border)" strokeWidth="1" />
                <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--text-4)">{fmtAxis(val, valueFormat)}</text>
              </g>
            );
          })}
          {/* bars */}
          {categories.map((cat, ci) => {
            if (stacked) {
              let acc = 0;
              return (
                <g key={ci}>
                  {vis.map(({ se, si }) => {
                    const v = Math.max(0, _num(se.values[ci]) || 0);
                    if (v <= 0) return null;
                    const yTop = yOf(acc + v);
                    const hgt = yOf(acc) - yTop;
                    acc += v;
                    return (
                      <rect key={si} x={x0(ci)} y={yTop} width={innerW} height={Math.max(0, hgt)}
                        fill={seriesColor(si)} rx="1" opacity={dimOf(si)}
                        {..._pickProps(onPick, () => onPick(ci, si))} />
                    );
                  })}
                </g>
              );
            }
            const sub = innerW / vis.length;
            return (
              <g key={ci}>
                {vis.map(({ se, si }, vi) => {
                  const v = Math.max(0, _num(se.values[ci]) || 0);
                  const y = yOf(v);
                  return (
                    <rect key={si} x={x0(ci) + vi * sub} y={y} width={Math.max(0, sub - 1)} height={padT + plotH - y}
                      fill={seriesColor(si)} rx="1" opacity={dimOf(si)}
                      {..._pickProps(onPick, () => onPick(ci, si))} />
                  );
                })}
              </g>
            );
          })}
          {/* x labels */}
          {categories.map((cat, ci) => (ci % showEveryX === 0) && (
            <text key={ci} x={padL + ci * bandW + bandW / 2} y={H - padB + 14} textAnchor="middle"
              fontSize="10" fill="var(--text-3)">
              {String(cat).length > 12 ? String(cat).slice(0, 11) + '…' : String(cat)}
            </text>
          ))}
        </svg>
      )}
      <ChartTip tip={tip ? { ...tip, content: tipContent } : null} containerW={W} />
      {series.length > 1 && <Legend items={seriesLegend(series, tog)} />}
    </div>
  );
};

// ── Horizontal bar (single series, good for many/long-label categories) ──────
const HorizontalBar = ({ categories, series, valueFormat, height, onPick }) => {
  const [ref, w] = useSize();
  const [tip, setTip] = React.useState(null);            // {ci, x, y}
  if (!categories.length || !series.length) return <EmptyChart />;
  const se = series[0];
  const rowH = 24, padL = 130, padR = 44, padT = 6, padB = 6;
  const H = padT + padB + categories.length * rowH;
  const W = Math.max(w, 240), plotW = W - padL - padR;
  const maxVal = Math.max(1, ...se.values.map((v) => Math.max(0, _num(v) || 0)));
  // Row-band tooltip (cursor y → row): shows the full untruncated label.
  const onMove = (e) => {
    const r = ref.current ? ref.current.getBoundingClientRect() : null;
    if (!r) return;
    const y = e.clientY - r.top;
    const ci = Math.floor((y - padT) / rowH);
    if (ci < 0 || ci >= categories.length) { setTip(null); return; }
    setTip({ ci, x: e.clientX - r.left, y });
  };
  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      {w > 0 && (
        <svg width={W} height={H} style={{ display: 'block', fontFamily: 'var(--font-ui)' }}
          onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
          {categories.map((cat, ci) => {
            const v = Math.max(0, _num(se.values[ci]) || 0);
            const bw = (v / maxVal) * plotW;
            const y = padT + ci * rowH;
            return (
              <g key={ci}>
                <text x={padL - 8} y={y + rowH / 2 + 3} textAnchor="end" fontSize="11" fill="var(--text-2)">
                  {String(cat).length > 20 ? String(cat).slice(0, 19) + '…' : String(cat)}
                </text>
                <rect x={padL} y={y + 3} width={Math.max(1, bw)} height={rowH - 8} fill={seriesColor(0)} rx="2"
                  {..._pickProps(onPick, () => onPick(ci, 0))} />
                <text x={padL + bw + 6} y={y + rowH / 2 + 3} fontSize="10.5" fill="var(--text-3)">{fmtVal(v, valueFormat)}</text>
              </g>
            );
          })}
        </svg>
      )}
      <ChartTip containerW={W} tip={tip ? { ...tip, content: (
        <div>
          <TipTitle>{String(categories[tip.ci])}</TipTitle>
          <TipRow color={seriesColor(0)} value={fmtVal(Math.max(0, _num(se.values[tip.ci]) || 0), valueFormat)} />
        </div>
      ) } : null} />
    </div>
  );
};

// ── Line / Area ──────────────────────────────────────────────────────────────
const LineChart = ({ categories, series, area, valueFormat, height = 280, onPick }) => {
  const [ref, w] = useSize();
  const tog = useSeriesToggle(series.map((s) => s.name));
  const [tip, setTip] = React.useState(null);            // {ci, x, y}
  if (!categories.length || !series.length) return <EmptyChart />;
  // Same original-index contract as BarChart: filter data, keep si for
  // seriesColor and onPick.
  let vis = series.map((se, si) => ({ se, si })).filter((v) => !tog.hidden.has(v.si));
  if (!vis.length) vis = series.map((se, si) => ({ se, si }));
  const padL = 52, padR = 12, padT = 12, padB = 46;
  const W = Math.max(w, 220), H = height;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const allVals = vis.flatMap((v) => v.se.values.map((x) => _num(x))).filter((x) => x != null);
  const maxVal = Math.max(1, ...allVals);
  const minVal = Math.min(0, ...allVals);
  const ticks = 4;
  const xOf = (i) => padL + (categories.length === 1 ? plotW / 2 : (i / (categories.length - 1)) * plotW);
  const yOf = (v) => padT + plotH - ((v - minVal) / (maxVal - minVal || 1)) * plotH;
  const showEveryX = Math.ceil(categories.length / Math.max(1, Math.floor(plotW / 64)));
  const dimOf = (si) => (tog.hot != null && tog.hot !== si ? 0.25 : 1);

  // Shared per-category tooltip: cursor x snaps to the nearest x-slot; the
  // point drill circles sit above and keep their own clicks.
  const onMove = (e) => {
    const r = ref.current ? ref.current.getBoundingClientRect() : null;
    if (!r) return;
    const x = e.clientX - r.left;
    if (x < padL - 8 || x > W - padR + 8) { setTip(null); return; }
    const ci = categories.length === 1 ? 0
      : Math.max(0, Math.min(categories.length - 1, Math.round(((x - padL) / (plotW || 1)) * (categories.length - 1))));
    setTip({ ci, x, y: e.clientY - r.top });
  };

  const tipContent = tip && (() => {
    const rows = vis.map((v) => ({ v, val: _num(v.se.values[tip.ci]) })).filter((x) => x.val != null);
    if (!rows.length) return null;
    return (
      <div>
        <TipTitle>{String(categories[tip.ci])}</TipTitle>
        {rows.map(({ v, val }) => (
          <TipRow key={v.si} color={seriesColor(v.si)} name={series.length > 1 ? v.se.name : null}
            value={fmtVal(val, valueFormat)} />
        ))}
      </div>
    );
  })();

  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      {w > 0 && (
        <svg width={W} height={H} style={{ display: 'block', fontFamily: 'var(--font-ui)' }}
          onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
          {Array.from({ length: ticks + 1 }).map((_, t) => {
            const val = minVal + ((maxVal - minVal) / ticks) * t;
            const y = yOf(val);
            return (
              <g key={t}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--border)" strokeWidth="1" />
                <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--text-4)">{fmtAxis(val, valueFormat)}</text>
              </g>
            );
          })}
          {vis.map(({ se, si }, vi) => {
            const pts = se.values.map((v, i) => (_num(v) == null ? null : [xOf(i), yOf(_num(v))])).filter(Boolean);
            if (!pts.length) return null;
            const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');
            const col = seriesColor(si);
            return (
              <g key={si} opacity={dimOf(si)}>
                {area && vi === 0 && (
                  <path d={`${d} L${pts[pts.length - 1][0]},${yOf(minVal)} L${pts[0][0]},${yOf(minVal)} Z`}
                    fill={col} opacity="0.12" />
                )}
                <path d={d} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                {se.values.map((v, i) => (_num(v) != null) && (
                  <g key={i}>
                    <circle cx={xOf(i)} cy={yOf(_num(v))} r={onPick ? 3 : 2.5} fill={col} />
                    {onPick && <circle cx={xOf(i)} cy={yOf(_num(v))} r="9" fill="transparent" style={{ cursor: 'pointer' }} onClick={() => onPick(i, si)} />}
                  </g>
                ))}
              </g>
            );
          })}
          {categories.map((cat, ci) => (ci % showEveryX === 0) && (
            <text key={ci} x={xOf(ci)} y={H - padB + 14} textAnchor="middle" fontSize="10" fill="var(--text-3)">
              {String(cat).length > 12 ? String(cat).slice(0, 11) + '…' : String(cat)}
            </text>
          ))}
        </svg>
      )}
      <ChartTip tip={tip ? { ...tip, content: tipContent } : null} containerW={W} />
      {series.length > 1 && <Legend items={seriesLegend(series, tog)} />}
    </div>
  );
};

// ── Pie / Donut ──────────────────────────────────────────────────────────────
// Pie legend row: click toggles the slice, double-click/alt-click isolates it,
// hover highlights it. (Drill stays on the slice paths themselves.)
const PieLegendRow = ({ label, pct, color, hidden, onToggle, onIsolate, onHover }) => {
  const gestures = useToggleGestures(onToggle, onIsolate);
  return (
    <span {...gestures}
      onMouseEnter={() => onHover(true)} onMouseLeave={() => onHover(false)}
      title="Click to toggle · double-click to isolate"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5,
        color: hidden ? 'var(--text-4)' : 'var(--text-2)',
        textDecoration: hidden ? 'line-through' : 'none', cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, background: color, opacity: hidden ? 0.3 : 1, flexShrink: 0 }} />
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(label)}</span>
      <span className="num" style={{ color: 'var(--text-3)' }}>{pct}</span>
    </span>
  );
};

const PieChart = ({ slices, donut, valueFormat, height = 280, onPick }) => {
  const tog = useSeriesToggle(slices.map((s) => String(s.label)));
  const wrapRef = React.useRef(null);
  const [tip, setTip] = React.useState(null);            // {i, x, y}
  if (!slices.length) return <EmptyChart />;
  const allTotal = slices.reduce((s, x) => s + Math.max(0, _num(x.value) || 0), 0);
  if (allTotal <= 0) return <EmptyChart message="All values are zero." />;
  // Hidden slices leave the pie entirely; the remaining shares re-normalize
  // against the VISIBLE total. Colors and onPick keep the original index i.
  const vis = slices.map((s, i) => ({ s, i })).filter((v) => !tog.hidden.has(v.i));
  const total = vis.reduce((s, v) => s + Math.max(0, _num(v.s.value) || 0), 0);
  const R = Math.min(height / 2 - 8, 120), cx = R + 8, cy = height / 2;
  const inner = donut ? R * 0.58 : 0;
  let angle = -Math.PI / 2;
  const arc = (val) => {
    const frac = val / total;
    const a0 = angle, a1 = angle + frac * 2 * Math.PI;
    angle = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    const [x0, y0] = p(R, a0), [x1, y1] = p(R, a1);
    if (inner > 0) {
      const [ix1, iy1] = p(inner, a1), [ix0, iy0] = p(inner, a0);
      return `M${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} L${ix1},${iy1} A${inner},${inner} 0 ${large} 0 ${ix0},${iy0} Z`;
    }
    return `M${cx},${cy} L${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} Z`;
  };
  const slicePct = (v) => (total > 0 ? Math.round((v / total) * 100) : 0);
  const tipContent = tip && (() => {
    const s = slices[tip.i];
    const v = Math.max(0, _num(s.value) || 0);
    return (
      <div>
        <TipTitle>{String(s.label)}</TipTitle>
        <TipRow color={seriesColor(tip.i)} value={fmtVal(v, valueFormat)} />
        <TipRow name="Share" value={`${slicePct(v)}%`} />
      </div>
    );
  })();
  return (
    <div ref={wrapRef} style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center', position: 'relative' }}>
      <svg width={(R + 8) * 2} height={height} style={{ display: 'block' }}>
        {total > 0 && vis.map(({ s, i }) => {
          const v = Math.max(0, _num(s.value) || 0);
          if (v <= 0) return null;
          return (
            <path key={i} d={arc(v)} fill={seriesColor(i)} stroke="var(--surface)" strokeWidth="1.5"
              opacity={tog.hot != null && tog.hot !== i ? 0.25 : 1}
              onMouseMove={(e) => {
                const r = wrapRef.current ? wrapRef.current.getBoundingClientRect() : null;
                if (r) setTip({ i, x: e.clientX - r.left, y: e.clientY - r.top });
              }}
              onMouseLeave={() => setTip(null)}
              {..._pickProps(onPick, () => onPick(i))} />
          );
        })}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 140 }}>
        {slices.map((s, i) => {
          const hidden = tog.hidden.has(i);
          const v = Math.max(0, _num(s.value) || 0);
          return (
            <PieLegendRow key={i} label={s.label} color={seriesColor(i)} hidden={hidden}
              pct={hidden ? '' : `${slicePct(v)}%`}
              onToggle={() => tog.toggle(i)} onIsolate={() => tog.isolate(i)}
              onHover={(on) => tog.setHot(on && !tog.hidden.has(i) ? i : null)} />
          );
        })}
      </div>
      <ChartTip tip={tip ? { ...tip, content: tipContent } : null}
        containerW={wrapRef.current ? wrapRef.current.clientWidth : 0} />
    </div>
  );
};

// ── Scatter ──────────────────────────────────────────────────────────────────
const ScatterChart = ({ points, xLabel, yLabel, valueFormat, height = 300, onPick }) => {
  const [ref, w] = useSize();
  const [tip, setTip] = React.useState(null);            // {p, x, y}
  const pts = points.filter((p) => _num(p.x) != null && _num(p.y) != null);
  if (!pts.length) return <EmptyChart />;
  const padL = 56, padR = 14, padT = 12, padB = 40;
  const W = Math.max(w, 240), H = height;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const xs = pts.map((p) => _num(p.x)), ys = pts.map((p) => _num(p.y));
  const xMin = Math.min(...xs), xMax = Math.max(...xs) || 1;
  const yMin = Math.min(...ys), yMax = Math.max(...ys) || 1;
  const xOf = (v) => padL + ((v - xMin) / (xMax - xMin || 1)) * plotW;
  const yOf = (v) => padT + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;
  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      {w > 0 && (
        <svg width={W} height={H} style={{ display: 'block', fontFamily: 'var(--font-ui)' }}>
          {Array.from({ length: 5 }).map((_, t) => {
            const val = yMin + ((yMax - yMin) / 4) * t; const y = yOf(val);
            return (<g key={t}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--border)" strokeWidth="1" />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--text-4)">{fmtAxis(val, valueFormat)}</text>
            </g>);
          })}
          {pts.map((p, i) => (
            <circle key={i} cx={xOf(_num(p.x))} cy={yOf(_num(p.y))} r={onPick ? 4.5 : 3.5} fill={seriesColor(0)} opacity="0.7"
              onMouseMove={(e) => {
                const r = ref.current ? ref.current.getBoundingClientRect() : null;
                if (r) setTip({ p, x: e.clientX - r.left, y: e.clientY - r.top });
              }}
              onMouseLeave={() => setTip(null)}
              {..._pickProps(onPick, () => onPick(p))} />
          ))}
          {xLabel && <text x={padL + plotW / 2} y={H - 6} textAnchor="middle" fontSize="10.5" fill="var(--text-3)">{xLabel}</text>}
        </svg>
      )}
      <ChartTip containerW={W} tip={tip ? { ...tip, content: (
        <div>
          {tip.p.label != null && <TipTitle>{String(tip.p.label)}</TipTitle>}
          <TipRow name={xLabel || 'x'} value={fmtVal(tip.p.x, 'number')} />
          <TipRow name={yLabel || 'y'} value={fmtVal(tip.p.y, valueFormat)} />
        </div>
      ) } : null} />
    </div>
  );
};

// ── KPI (single stat) ────────────────────────────────────────────────────────
const KpiCard = ({ value, label, format, onPick }) => (
  <div onClick={onPick || undefined} title={onPick ? 'Click to see the underlying data' : undefined}
    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 140, gap: 6, padding: 20, cursor: onPick ? 'pointer' : 'default' }}>
    <div style={{ fontSize: 44, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.02em', lineHeight: 1 }}>
      {fmtVal(value, format)}
    </div>
    {label && <div style={{ fontSize: 12.5, color: 'var(--text-3)', textAlign: 'center' }}>{label}</div>}
  </div>
);

// ── Data table (reuses table_helpers) ────────────────────────────────────────
const _ENTITY_PATCH = { companies: 'updateCompany', contacts: 'updateContact', engagements: 'updateEngagement' };

const DataTableWidget = ({ columns, rows, valueFormat, pageSize = 50, onRowClick, entityHint, hideIds, showTotals, editable, maxHeight,
  onLoadMore, loadingMore, total }) => {
  // Inline-editable record list: which columns are editable, and the per-row PK
  // column to PATCH. `edits` overlays committed values until the next run.
  const editMap = React.useMemo(() => {
    const m = {}; ((editable && editable.fields) || []).forEach((f) => { m[f.column] = f; }); return m;
  }, [editable]);
  const idCol = editable && editable.id_column;
  const [edits, setEdits] = React.useState({});
  // Which record a given cell writes to. Most cells write the row's own record,
  // but a field may name its own `entity` + `id_column` — that's how a joined
  // display column (Company on a pipeline grid) edits the company it points at
  // instead of the engagement the row is.
  const fieldTarget = (f, row) => {
    const col = (f && f.id_column) || idCol;
    return { entity: (f && f.entity) || (editable && editable.entity), id: col ? row[col] : null };
  };
  // Overlay key: the RECORD the value belongs to, so every row pointing at the
  // same company reflects a rename, not just the one that was edited.
  const editKey = (f, row, col) => {
    const { id } = fieldTarget(f, row);
    return id == null ? null : `${id}|${col || (f && f.column)}`;
  };
  // Owner/dealmaker cells are user pickers, which need the roster. Loaded lazily
  // and only when some field actually asks for it (the API caches for 5 min).
  const needsRoster = React.useMemo(
    () => ((editable && editable.fields) || []).some((f) => f.type === 'user'), [editable]);
  const [roster, setRoster] = React.useState([]);
  React.useEffect(() => {
    if (!needsRoster) return undefined;
    let alive = true;
    window.AtlasData.loadTeamMembers()
      .then((m) => { if (alive && Array.isArray(m)) setRoster(m); })
      .catch(() => {});
    return () => { alive = false; };
  }, [needsRoster]);
  const saveCell = async (row, f, next) => {
    const { entity, id } = fieldTarget(f, row);
    const fn = _ENTITY_PATCH[entity];
    if (!fn || !window.AtlasApi[fn] || id == null) throw new Error('Not editable.');
    await window.AtlasApi[fn](id, { [f.field]: next });
    // A user picker stores an id but the column displays a name — write the
    // fresh name into the overlay so the cell reads back as a person.
    const shown = f.type === 'user'
      ? ((roster.find((u) => u.id === next) || {}).full_name || null)
      : next;
    setEdits((e) => ({
      ...e,
      [editKey(f, row, f.column)]: shown,
      ...(f.value_column ? { [editKey(f, row, f.value_column)]: next } : {}),
    }));
  };
  // A next action is NOT a PATCH: Atlas models it as a note, and the server
  // replaces the engagement's next_action_notes/date off that note while
  // keeping the previous value as history and mirroring it to DealCloud.
  // So a 'next_action' cell POSTs a note and then overlays BOTH columns
  // locally (the row's own next_action_date, if the table shows it) so the
  // cell reads back without re-running the whole widget.
  const saveNextAction = async (row, f, { body, date }) => {
    const { entity, id: rowId } = fieldTarget(f, row);
    if (entity !== 'engagements' || rowId == null) throw new Error('Next actions belong to engagements.');
    const uid = (() => { try { return localStorage.getItem('atlas.activeUserId'); } catch (_) { return null; } })();
    await window.AtlasApi.createNote({
      body,
      note_type: 'general',
      dc_note_subtype: 'Next Action',
      next_action_date: date || null,
      engagement_id: rowId,
      company_id: row.company_id || null,
      author_id: uid || null,
    });
    setEdits((e) => ({
      ...e,
      [`${rowId}|${f.column}`]: body,
      ...(f.date_column ? { [`${rowId}|${f.date_column}`]: date || null } : {}),
    }));
  };

  const cols = React.useMemo(() => {
    const explicit = !!(columns && columns.length);
    let base = explicit
      ? columns
      : (rows.length ? Object.keys(rows[0]).map((k) => ({ key: k, label: k })) : []);
    // Hide opaque FK/id columns. Defaults ON when falling back to raw result
    // keys (no explicit column list, e.g. the AI omitted table_columns) —
    // pass hideIds={false} to keep them. Kept: entity links still work; they
    // read the hidden `<x>_id` from the row itself (colSet below), not from
    // the displayed columns.
    if (hideIds != null ? hideIds : !explicit) {
      const kept = base.filter((c) => !/(^id$|_id$)/i.test(c.key));
      if (kept.length) base = kept;   // never filter down to zero columns
    }
    if (idCol) base = base.filter((c) => c.key !== idCol);                 // hide the PK driving edits
    // Humanize headers unless an explicit (non-raw) label was provided.
    return base.map((c) => ({ ...c, label: (c.label && c.label !== c.key) ? c.label : humanizeCol(c.key) }));
  }, [columns, rows, hideIds, idCol]);

  // Full key set from the row (not the display cols) so linking can still read
  // a hidden `<x>_id` behind a visible `<x>_name`.
  const colSet = React.useMemo(
    () => new Set(rows.length ? Object.keys(rows[0]) : (columns || []).map((c) => c.key)),
    [rows, columns]);
  // The DISPLAYED keys (colSet minus the hidden ones) — used to decide whether
  // a next-action cell should print the due date itself or leave it to the
  // table's own date column.
  const colKeys = React.useMemo(() => new Set(cols.map((c) => c.key)), [cols]);
  // A row value with any committed inline edit applied on top. Edits are keyed
  // by the record the value belongs to (the row's own, or a joined one for a
  // cross-entity field), so a rename shows on every row pointing at it.
  const editedVal = (r, col) => {
    const k = editKey(editMap[col], r, col);
    return k && k in edits ? edits[k] : r[col];
  };

  const isNumCol = React.useMemo(() => {
    const m = {};
    for (const c of cols) {
      const vals = rows.slice(0, 12).map((r) => r[c.key]).filter((v) => v != null);
      m[c.key] = vals.length > 0 && vals.every((v) => _num(v) != null);
    }
    return m;
  }, [cols, rows]);

  const EXTRACT = React.useMemo(() => {
    const m = {};
    for (const c of cols) m[c.key] = (r) => (isNumCol[c.key] ? _num(r[c.key]) : (r[c.key] == null ? null : String(r[c.key]).toLowerCase()));
    return m;
  }, [cols, isNumCol]);

  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState('desc');
  const [limit, setLimit] = React.useState(pageSize);
  const toggleSort = React.useMemo(() => window.makeToggleSort(setSortKey, setSortDir), []);

  const sorted = React.useMemo(() => {
    if (!sortKey) return rows;
    return rows.slice().sort(window.makeComparator(sortKey, sortDir, EXTRACT));
  }, [rows, sortKey, sortDir, EXTRACT]);
  const shown = sorted.slice(0, limit);

  // Optional per-column totals: sum of each numeric column over ALL rows (not
  // just the shown page), rendered as a summary strip under the header. Percent
  // columns are skipped (summing percentages is meaningless).
  const colTotals = React.useMemo(() => {
    if (!showTotals) return null;
    const numCols = cols.filter((c) => isNumCol[c.key] && c.format !== 'percent');
    if (!numCols.length) return null;
    const t = {};
    for (const c of numCols) t[c.key] = rows.reduce((s, r) => s + (_num(r[c.key]) || 0), 0);
    return t;
  }, [showTotals, cols, isNumCol, rows]);

  if (!rows.length) return <EmptyChart message="No rows." />;

  const cellFmt = (c, v) => {
    const f = c.format || (isNumCol[c.key] ? (valueFormat === 'currency' ? 'currency' : 'number') : 'text');
    if (v == null || v === '') return '—';
    if (f === 'currency') return fmtVal(v, 'currency');
    if (f === 'percent') return fmtVal(v, 'percent');
    if (f === 'date') return window.fmtDate(v);
    if (f === 'number') return fmtVal(v, 'number');
    return String(v);
  };

  // Shared grid template for header + every row → columns line up by
  // construction. minmax(min, …fr) lets cells share width by weight (numbers
  // narrower) and shrink with an ellipsis — but never below a readable minimum:
  // on a narrow card the grid overflows into the horizontal-scroll wrapper
  // below instead of crushing every column to a few px. The first text column
  // (usually the name) gets a little extra room.
  // When rows are clickable (drill to underlying records), reserve a trailing
  // chevron column so the interaction is discoverable, not just a cursor change.
  const drillable = !!onRowClick;
  const totalsLabelIdx = cols.findIndex((c) => !isNumCol[c.key]);   // first text column → "Total" label
  const colMin = (c, i) => (isNumCol[c.key] ? 80 : (i === totalsLabelIdx ? 140 : 110));
  const tpl = [
    ...cols.map((c, i) => `minmax(${colMin(c, i)}px, ${isNumCol[c.key] ? '0.8fr' : '1.4fr'})`),
    ...(drillable ? ['22px'] : []),
  ].join(' ');
  const cellBase = { padding: '7px 10px', fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  // Header/totals/rows are sibling grids sharing `tpl` — fixed px minimums plus
  // this shared minWidth keep their tracks aligned, so they scroll as one.
  const minTableW = cols.reduce((s, c, i) => s + colMin(c, i), 0) + (drillable ? 22 : 0);

  return (
    <div style={{ overflow: 'auto', maxHeight: maxHeight || undefined }}>
      <div style={{ minWidth: minTableW }}>
      <div style={{ display: 'grid', gridTemplateColumns: tpl, borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', position: 'sticky', top: 0, zIndex: 2 }}>
        {cols.map((c) => {
          const num = isNumCol[c.key];
          const active = sortKey === c.key;
          return (
            <button key={c.key} onClick={() => toggleSort(c.key)} title={`Sort by ${c.label}`}
              style={{ ...cellBase, display: 'flex', alignItems: 'center', gap: 4, justifyContent: num ? 'flex-end' : 'flex-start',
                border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, color: active ? 'var(--text)' : 'var(--text-3)', textAlign: num ? 'right' : 'left' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label}</span>
              {active && <span style={{ color: 'var(--text-4)', flexShrink: 0 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>}
            </button>
          );
        })}
        {drillable && <div aria-hidden="true" style={{ ...cellBase, padding: '7px 6px' }} />}
      </div>
      {colTotals && (
        <div style={{ display: 'grid', gridTemplateColumns: tpl, borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
          {cols.map((c, i) => {
            const num = isNumCol[c.key];
            return (
              <div key={c.key} className={num ? 'num' : ''} style={{ ...cellBase, color: 'var(--text)', fontWeight: 600,
                textAlign: num ? 'right' : 'left', fontVariantNumeric: num ? 'tabular-nums' : 'normal' }}>
                {colTotals[c.key] != null
                  ? cellFmt(c, colTotals[c.key])
                  : (i === totalsLabelIdx ? <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>{`Total · ${rows.length.toLocaleString()} rows`}</span> : '')}
              </div>
            );
          })}
          {drillable && <div aria-hidden="true" style={{ ...cellBase, padding: '7px 6px' }} />}
        </div>
      )}
      {/* Key editable rows by their RECORD id, not the list index. A widget can
          re-run under an open editor (a dashboard filter applied, "Refresh",
          the auto-refresh tick) — with index keys the still-mounted editor
          keeps its draft but is handed whatever row now sits at that index, so
          the save lands on a DIFFERENT record. Keying by id means such a row
          unmounts instead: the draft is lost, which is the safe failure. */}
      {shown.map((r, ri) => (
        <div key={idCol && r[idCol] != null ? String(r[idCol]) : ri}
          onClick={onRowClick ? () => onRowClick(r) : undefined}
          onMouseEnter={onRowClick ? (e) => { e.currentTarget.style.background = 'var(--surface-hover)'; const c = e.currentTarget.querySelector('.drill-caret'); if (c) c.style.color = 'var(--accent-text)'; } : undefined}
          onMouseLeave={onRowClick ? (e) => { e.currentTarget.style.background = 'transparent'; const c = e.currentTarget.querySelector('.drill-caret'); if (c) c.style.color = 'var(--text-4)'; } : undefined}
          style={{ display: 'grid', gridTemplateColumns: tpl, borderBottom: '1px solid var(--border)', cursor: onRowClick ? 'pointer' : 'default' }}>
          {cols.map((c) => {
            const num = isNumCol[c.key];
            const ef = editMap[c.key];
            // Editable when the field's target record is actually on this row —
            // a cross-entity cell (Company) needs its own id present, and a
            // joined column with no id behind it stays read text.
            const editing = !!(ef && fieldTarget(ef, r).id != null);
            const link = cellLink(c.key, r, colSet, entityHint);
            // Overlay committed edits on EVERY column, not just editable ones:
            // saving a next action also moves its due date, which is usually a
            // separate read-only column in the same row.
            const cellVal = editedVal(r, c.key);
            // Editable + linkable → hybrid: the name text navigates, the cell
            // (and its hover pencil) opens the editor. displayAs swaps
            // EditableCell's read view for the link without touching editing.
            const linkView = link ? (
              <span onClick={(e) => { e.stopPropagation(); navTo(link.method, link.id, editing ? cellVal : link.name); }}
                title={link.method === 'goToUser' ? 'Open profile' : 'Open record'}
                style={{ color: 'var(--accent-text)', cursor: 'pointer' }}
                onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}>
                {editing ? (cellVal == null || cellVal === '' ? '—' : String(cellVal)) : cellFmt(c, cellVal)}
              </span>
            ) : null;
            return (
              <div key={c.key} className={num ? 'num' : ''}
                onClick={editing ? (e) => e.stopPropagation() : undefined}
                style={{
                  ...cellBase, color: 'var(--text-2)', textAlign: num ? 'right' : 'left',
                  fontVariantNumeric: num ? 'tabular-nums' : 'normal',
                }}>
                {editing && ef.type === 'next_action' && window.NextActionCell ? (
                  // Note-backed cell: the read view is the current next action,
                  // the "+" opens the same composer the Engagements grid uses.
                  // The due date is suppressed when this table already shows it
                  // in its own column, so it isn't printed twice.
                  <window.NextActionCell
                    note={cellVal}
                    date={ef.date_column ? editedVal(r, ef.date_column) : null}
                    showDate={!(ef.date_column && colKeys.has(ef.date_column))}
                    onSave={(payload) => saveNextAction(r, ef, payload)} />
                ) : editing && ef.type !== 'next_action' ? (
                  // `value_column` lets a cell DISPLAY one column and EDIT
                  // another: Owner shows the dealmaker's name (sortable,
                  // exportable) while the picker reads and writes dealmaker_id.
                  <window.EditableCell type={ef.type} fill align={num ? 'right' : 'left'}
                    value={ef.value_column ? editedVal(r, ef.value_column) : cellVal}
                    options={ef.options}
                    users={ef.type === 'user' ? roster : undefined}
                    displayAs={ef.value_column || linkView
                      ? (linkView || <span>{cellFmt(c, cellVal)}</span>)
                      : undefined}
                    onSave={(nv) => saveCell(r, ef, nv)} />
                ) : linkView || cellFmt(c, cellVal)}
              </div>
            );
          })}
          {drillable && (
            <div aria-hidden="true" className="drill-caret"
              style={{ ...cellBase, padding: '7px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: 'var(--text-4)', fontSize: 15, lineHeight: 1 }}>›</div>
          )}
        </div>
      ))}
      {(sorted.length > limit || onLoadMore) && (
        // Two stages behind one button: reveal more of what is already loaded,
        // and once that is exhausted fetch the next page from the server. The
        // count is honest about which is on offer — `total` (a true COUNT(*))
        // when the server has more, otherwise just what is held here.
        <button
          disabled={!!loadingMore}
          onClick={() => (sorted.length > limit
            ? setLimit((l) => l + pageSize)
            : onLoadMore && onLoadMore())}
          style={{
            width: '100%', padding: '8px', border: 'none', borderTop: '1px solid var(--border)',
            background: 'var(--surface)', cursor: loadingMore ? 'default' : 'pointer',
            fontSize: 11.5, color: loadingMore ? 'var(--text-3)' : 'var(--accent-text)',
          }}>
          {loadingMore ? 'Loading…'
            : sorted.length > limit ? `Show more (${sorted.length - limit} more)`
              : total ? `Load more (${sorted.length} of ${total.toLocaleString()})`
                : 'Load more'}
        </button>
      )}
      </div>
    </div>
  );
};

// ── Dispatcher ───────────────────────────────────────────────────────────────
const WidgetRenderer = ({ spec, data, height, onSelect, onLoadMore, loadingMore }) => {
  const columns = (data && data.columns) || [];
  const rows = (data && data.rows) || [];
  const [view, setView] = React.useState('chart');   // 'chart' | 'table'
  if (!spec) return <EmptyChart message="No spec." />;
  const t = spec.chart_type || 'table';
  const fmt = spec.value_format || 'number';

  if (!rows.length) return <EmptyChart message="The query returned no rows." />;

  // Drill-down: charts call onPick when an element is clicked; we normalize that
  // into a selection {dimension, value, series*, measure*, rows} and hand it up.
  const emit = onSelect ? (sel) => onSelect({ format: fmt, columns, ...sel }) : null;

  const renderChart = () => {
    try {
      if (t === 'kpi') {
        const { y } = inferAxes(spec, columns, rows);
        const col = (y && y[0]) || columns[0];
        return <KpiCard value={rows[0] ? rows[0][col] : null} label={spec.description || humanizeCol(col)} format={fmt}
          onPick={emit ? () => emit({ measure: col, measureValue: rows[0] ? rows[0][col] : null, rows }) : null} />;
      }
      if (t === 'table') {
        const cols = (spec.table_columns && spec.table_columns.length)
          ? spec.table_columns.map((c) => ({ key: c.key, label: c.label || c.key, format: c.format }))
          : null;
        // The entity this table's rows represent (record lists know it from
        // their editable/query source) → bare `name` cells link to the record.
        const hint = sourceEntity((spec.editable && spec.editable.entity) || (spec.query && spec.query.source));
        // Editable record lists drive cell editing off spec.editable; row-click
        // drill is disabled for them (the rows already ARE the records).
        // `height` (set on the dashboard canvas / fullscreen) caps the table so
        // it scrolls inside the card; surfaces that omit it keep auto height.
        return <DataTableWidget columns={cols} rows={rows} valueFormat={fmt} showTotals editable={spec.editable || null}
          entityHint={hint} maxHeight={height}
          onLoadMore={onLoadMore} loadingMore={loadingMore} total={data && data.total}
          onRowClick={(emit && !spec.editable) ? (row) => emit({ rows: [row], row }) : null} />;
      }
      const { x, y, series } = inferAxes(spec, columns, rows);
      const matchCat = (cat, sName) => rows.filter((r) =>
        String(r[x]) === String(cat) && (series ? String(r[series] ?? '—') === String(sName) : true));
      if (t === 'pie' || t === 'donut') {
        const yc = (y && y[0]) || columns.find((c) => c !== x);
        const slices = rows.map((r) => ({ label: r[x], value: r[yc], row: r }));
        return <PieChart slices={slices} donut={t === 'donut'} valueFormat={fmt} height={height}
          onPick={emit ? (i) => emit({ dimension: x, value: slices[i].label, measure: yc, measureValue: slices[i].value, rows: [slices[i].row] }) : null} />;
      }
      if (t === 'scatter') {
        const yc = (y && y[0]);
        const pts = rows.map((r) => ({ x: r[x], y: r[yc], label: r[x], row: r }));
        return <ScatterChart points={pts} xLabel={humanizeCol(x)} yLabel={humanizeCol(yc)} valueFormat={fmt} height={height}
          onPick={emit ? (p) => emit({ dimension: x, value: p.label, measure: yc, measureValue: p.y, rows: [p.row] }) : null} />;
      }
      const piv = pivot(rows, x, y, series);
      const onBar = emit ? (ci, si) => emit({
        dimension: x, value: piv.categories[ci],
        seriesKey: series || null, seriesValue: piv.series[si] ? piv.series[si].name : null,
        measure: (y && y[0]) || null, measureValue: piv.series[si] ? piv.series[si].values[ci] : null,
        rows: matchCat(piv.categories[ci], piv.series[si] && piv.series[si].name),
      }) : null;
      if (t === 'horizontal_bar') return <HorizontalBar {...piv} valueFormat={fmt} height={height} onPick={onBar} />;
      if (t === 'line') return <LineChart {...piv} valueFormat={fmt} height={height} onPick={onBar} />;
      if (t === 'area') return <LineChart {...piv} area valueFormat={fmt} height={height} onPick={onBar} />;
      if (t === 'stacked_bar') return <BarChart {...piv} stacked valueFormat={fmt} height={height} onPick={onBar} />;
      // bar (default)
      return <BarChart {...piv} valueFormat={fmt} height={height} onPick={onBar} />;
    } catch (e) {
      // Never crash a dashboard on one bad widget — fall back to the raw table.
      return (
        <div>
          <div style={{ fontSize: 11, color: 'var(--rose-fg)', padding: '6px 10px' }}>
            Couldn't render this chart ({String(e.message || e)}). Showing the data instead.
          </div>
          <DataTableWidget columns={null} rows={rows} valueFormat={fmt} maxHeight={height} />
        </div>
      );
    }
  };

  // Any chart can be flipped to a sortable table of its own rows (click a column
  // header to order). kpi (one number) and table (already a table) don't toggle.
  const canToggle = !['kpi', 'table'].includes(t);
  const body = (canToggle && view === 'table')
    ? <DataTableWidget columns={null} rows={rows} valueFormat={fmt} showTotals maxHeight={height}
        onRowClick={emit ? (row) => emit({ rows: [row], row }) : null} />
    : renderChart();
  if (!canToggle) return body;

  const ChartIco = (window.Icon && window.Icon.forChart) ? window.Icon.forChart(t) : null;
  const TableIco = window.Icon && window.Icon.Table;
  const seg = (v, Ico) => (
    <button key={v} onClick={(e) => { e.stopPropagation(); setView(v); }} title={v === 'chart' ? 'Chart view' : 'Table view'}
      style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', border: 'none', cursor: 'pointer', fontSize: 10.5, fontWeight: 500,
        background: view === v ? 'var(--accent-soft)' : 'transparent', color: view === v ? 'var(--accent-text)' : 'var(--text-3)' }}>
      {Ico ? <Ico size={12} /> : (v === 'chart' ? 'Chart' : 'Table')}
    </button>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <div onMouseDown={(e) => e.stopPropagation()} draggable={false} title="Chart / table view"
          style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {seg('chart', ChartIco)}
          {seg('table', TableIco)}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{body}</div>
    </div>
  );
};

const CHART_TYPES = [
  { value: 'bar', label: 'Bar' },
  { value: 'horizontal_bar', label: 'Horizontal bar' },
  { value: 'stacked_bar', label: 'Stacked bar' },
  { value: 'line', label: 'Line' },
  { value: 'area', label: 'Area' },
  { value: 'pie', label: 'Pie' },
  { value: 'donut', label: 'Donut' },
  { value: 'scatter', label: 'Scatter' },
  { value: 'kpi', label: 'Single stat' },
  { value: 'table', label: 'Table' },
];

Object.assign(window, {
  WidgetRenderer, KpiCard, DataTableWidget, BarChart, LineChart, PieChart,
  ScatterChart, HorizontalBar, pivot, aggregate, inferAxes, CHART_TYPES,
  humanizeCol,
  AnalyticsChartUtils: { fmtVal, fmtAxis, seriesColor, _num, humanizeCol, sourceEntity },
});
